import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { uniq, flatMap, isEmpty, isEqual, get } from 'lodash';
import InstallerForm from './components/installerForm';
import {
  updateInstaller,
  requestInstaller,
  clearValidationErrors,
  navigateBack,
  uploadInstallerPayRate,
  toggleInstallerPayRateUploadDialog,
  requestInstallerPayRateUploads,
  setPayRateUploadErrorMessage,
  addEventToInstaller,
} from 'actionCreators';
import {
  getInstallerErrors,
  getInstallerById,
  getHelperById,
  getCrewById,
  getWorkroomById,
  getWorkroomList,
  getPayRateUploadsError,
  getPayRateUploadsLoading,
  getPayRateUploadsShowDialog,
  getRole,
} from '../../../../reducers';
import connectUI from 'redux-ui';
import Loading from 'components/Loading';
import getPayRateUploadsData from './payRateUploads';
import { RoleType } from 'types/user';
import WorkroomType from 'types/workroom';

class InstallerEdit extends Component {
  constructor(props) {
    super(props);
    this.state = { crewChanges: {}, crewPayId: null };
    this.handleSave = this.handleSave.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handlePayRateUpload = this.handlePayRateUpload.bind(this);
    this.updateCrewChanges = this.updateCrewChanges.bind(this);
    this.handleCancel = () => { this.props.navigateBack(); };
    this.handleCrewPayId = this.handleCrewPayId.bind(this);
  }

  componentDidMount = () => Promise.all([
    this.props.requestInstaller(this.props.id),
    this.props.requestInstallerPayRateUploads(this.props.id),
  ]);

  componentWillUnmount = () => this.props.clearValidationErrors('INSTALLER');

  updateCrewChanges(key, value, nestedFieldId) {
    const newCrewChanges = Object.assign({}, this.state.crewChanges);
    newCrewChanges[nestedFieldId] = {
      ...newCrewChanges[nestedFieldId],
      [key]: value,
    };
    this.setState({ crewChanges: newCrewChanges });
  }

  createCrewDiff = (crew, crewChanges) => {
    const crewDiff = {};
    const changedVals = Object.keys(crewChanges[crew.id]);
    changedVals.forEach((val) => {
      const oldVal = crew[val];
      const newVal = get(crewChanges, [crew.id, val], 'null');
      if (!isEqual(oldVal, newVal)) crewDiff[val] = [oldVal, newVal];
    });
    return crewDiff;
  }

  createProfileDiff = () => {
    const { crewChanges } = this.state;
    const { crews } = this.props;
    const profileDiff = {};
    const changedCrews = Object.keys(crewChanges).map(crew_id => parseInt(crew_id, 10));
    crews.forEach((crew) => {
      if (changedCrews.includes(crew.id)) {
        const crewDiff = this.createCrewDiff(crew, crewChanges);
        if (!isEmpty(crewDiff)) {
          profileDiff.crew = { ...profileDiff.crew, [crew.name]: {} };
          profileDiff.crew[crew.name] = crewDiff;
        }
      }
    });
    return profileDiff;
  }

  handleSave = (installer) => {
    let profileDiff = {};
    let eventProps = {};
    if (!isEmpty(this.state.crewChanges)) {
      profileDiff = this.createProfileDiff();
      eventProps = {
        name: 'Update Profile',
        kind: 'update_profile',
        data: { updates: profileDiff },
      };
    }
    return this.props.updateInstaller(installer)
      .then(() => {
        if (!isEmpty(profileDiff)) this.props.addEventToInstaller(installer.id, eventProps);
        this.props.updateUI('successMessage', 'Changes have been saved');
      }).catch((e) => { throw e; });
  }

  handleTabChange = tabIndex => this.props.updateUI('activeTab', tabIndex);

  handlePayRateUpload = formData => this.props.uploadInstallerPayRate(
    this.props.id, formData, this.state.crewPayId === 'i' ? null : this.state.crewPayId);

  handleCrewPayId = val => this.setState({ crewPayId: val.value });

  render = () => {
    const { errors, installer, loading, helpers, crews, allWorkrooms, ui, uploadError,
      fileLoading, showUploadDialog, payRateUploadsData, role, user, workrooms } = this.props;
    return (loading ? <Loading /> : <InstallerForm
      errors={errors}
      onSave={this.handleSave}
      updateCrewChanges={this.updateCrewChanges}
      onCancel={this.handleCancel}
      onPayRateUpload={this.handlePayRateUpload}
      handleCrewPayId={this.handleCrewPayId}
      installer={installer}
      helpers={helpers}
      crewPayId={this.state.crewPayId}
      crews={crews}
      allWorkrooms={allWorkrooms}
      workrooms={workrooms}
      activeTab={ui.activeTab}
      onTabChange={this.handleTabChange}
      payRateUploadsData={payRateUploadsData}
      uploadError={uploadError}
      fileLoading={fileLoading}
      showUploadDialog={showUploadDialog}
      onUploadDialogToggle={this.props.toggleInstallerPayRateUploadDialog}
      setUploadErrorMessage={this.props.setPayRateUploadErrorMessage}
      role={role}
      user={user}
      installerFormType={'Edit'}
    />);
  }
}

InstallerEdit.propTypes = {
  id: PropTypes.number,
  navigateBack: PropTypes.func,
  requestInstaller: PropTypes.func,
  requestWorkroomList: PropTypes.func,
  clearValidationErrors: PropTypes.func,
  updateInstaller: PropTypes.func,
  addEventToInstaller: PropTypes.func,
  updateUI: PropTypes.func,
  errors: PropTypes.object,
  installer: PropTypes.object,
  loading: PropTypes.bool,
  helpers: PropTypes.array,
  crews: PropTypes.array,
  workrooms: PropTypes.arrayOf(WorkroomType),
  allWorkrooms: PropTypes.array,
  ui: PropTypes.object,
  uploadError: PropTypes.object,
  fileLoading: PropTypes.bool,
  uploadInstallerPayRate: PropTypes.func,
  toggleInstallerPayRateUploadDialog: PropTypes.func,
  showUploadDialog: PropTypes.bool,
  payRateUploadsData: PropTypes.array,
  requestInstallerPayRateUploads: PropTypes.func,
  setPayRateUploadErrorMessage: PropTypes.func,
  role: PropTypes.string.isRequired,
  user: RoleType,
};

const mapStateToProps = (state, ownProps) => {
  const id = Number(ownProps.params.id);
  const installer = getInstallerById(state)[id];
  const allWorkrooms = getWorkroomList(state);
  const loading = (installer === undefined || installer.loading) ||
                  (allWorkrooms === undefined || allWorkrooms.length === 0);
  const helpers = (installer && installer.helpers ?
                    (installer.helpers.map(helperId => getHelperById(state)(helperId)))
                    : []);
  const crews = (installer && installer.crews ?
                    (installer.crews.map(crewId => getCrewById(state)(crewId)))
                    : []);
  const crewsWorkrooms = {};
  crews.forEach((crew) => {
    crewsWorkrooms[crew.id] = crew.workrooms.map(wrId => getWorkroomById(state)(wrId));
  });
  const workrooms = uniq(flatMap(crewsWorkrooms));
  return {
    id,
    loading,
    installer,
    errors: getInstallerErrors(state) || {},
    helpers,
    crews,
    workrooms,
    allWorkrooms,
    uploadError: getPayRateUploadsError(state),
    fileLoading: getPayRateUploadsLoading(state),
    showUploadDialog: getPayRateUploadsShowDialog(state),
    payRateUploadsData: getPayRateUploadsData(state, id),
    role: getRole(state),
    user: state.user,
  };
};

export default connectUI()(connect(
  mapStateToProps, {
    navigateBack,
    updateInstaller,
    clearValidationErrors,
    requestInstaller,
    uploadInstallerPayRate,
    toggleInstallerPayRateUploadDialog,
    requestInstallerPayRateUploads,
    setPayRateUploadErrorMessage,
    addEventToInstaller,
  },
)(InstallerEdit));



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/installers/installerEdit.jsx